import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Alert, Button, Descriptions, Form, Input, Modal, Popconfirm, Select, Space, Spin, Table, Tag,
  Tooltip, Typography,
} from "antd";
import { ArrowLeftOutlined, DeleteOutlined, UserAddOutlined } from "@ant-design/icons";
import actions from "../../redux/admin/actions";
import withRouter from "../../hoc/withRouter";
import { adminTimestampMeta, formatAdminDate, formatAdminDateTime } from "../../dates";
import { showAdminFeedback } from "../../feedback";
import Wrapper from "./index.style";

const STATUS_COLORS = {
  trial: "blue", pending_invoice: "orange", active: "green", suspended: "red", cancelled: "default",
};
const {
  fetchOrganization,
  inviteOrganization,
  resendInvitation,
  deleteMember,
  clearFeedback,
} = actions;

function invitationStatus(invite) {
  if (invite.revoked_at) return "revoked";
  return invite.pending ? "pending" : "expired";
}

export class OrganizationDetailsPanel extends Component {
  state = { inviteOpen: false };

  inviteFormRef = React.createRef();

  componentDidMount() {
    this.fetchOrganization();
  }

  componentDidUpdate(prevProps) {
    const previousId = this.organizationId(prevProps);
    const currentId = this.organizationId();
    if (currentId && currentId !== previousId) this.props.fetchOrganization(currentId);

    const { feedback, clearFeedback, t } = this.props;
    if (feedback && feedback !== prevProps.feedback) {
      showAdminFeedback(feedback, t);
      if (feedback.type !== "error" && feedback.operation === "invitationSent") {
        this.setState({ inviteOpen: false });
        this.inviteFormRef.current?.resetFields();
      }
      clearFeedback();
    }
  }

  organizationId = (props = this.props) => props.router.params.organizationSlug?.match(/^\d+/)?.[0];

  fetchOrganization = () => {
    const id = this.organizationId();
    if (id) this.props.fetchOrganization(id);
  };

  sendInvite = async () => {
    const invite = await this.inviteFormRef.current.validateFields();
    this.props.inviteOrganization(this.props.detail.organization.id, invite, "detail");
  };

  resendInvite = (invite) => {
    this.props.resendInvitation(
      this.props.detail.organization.id, invite.id, invite.email, "detail",
    );
  };

  deleteMember = (member) => {
    this.props.deleteMember(
      this.props.detail.organization.id, member.id, this.personName(member),
    );
  };

  personName = (person) =>
    `${person.first_name || ""} ${person.last_name || ""}`.trim() || person.email;

  // gos-admin's table idiom: lead with "how long ago", keep the exact
  // moment a hover away.
  renderTimestamp = (value) => {
    const { detail, i18n } = this.props;
    const { isValid, relative, full } = adminTimestampMeta(
      value, detail.organization.timezone, i18n.language,
    );

    return isValid ? <Tooltip title={full}>{relative}</Tooltip> : relative;
  };

  render() {
    const {
      t, i18n, detail, loading, requestError, mutationLoading, operator, router,
    } = this.props;
    const { inviteOpen } = this.state;
    const organizationId = this.organizationId();
    const error = !organizationId ? t("admin.organizationDetails.invalidAddress") : requestError;
    const navigateBack = () => router.navigate("/admin/organizations");

    if (error) {
      return (
        <Wrapper>
          <Space direction="vertical" size="middle" className="error-stack">
            <Button icon={<ArrowLeftOutlined />} onClick={navigateBack}>{t("admin.organizationDetails.back")}</Button>
            <Alert type="error" message={error} showIcon />
          </Space>
        </Wrapper>
      );
    }
    if (loading || !detail) return <Spin />;

    const { organization } = detail;
    // One row per person. An invitation that was accepted or revoked, or
    // whose recipient joined by some other route, is history rather than
    // an outstanding seat — listing those next to the membership is what
    // made the same person appear several times here.
    const memberEmails = new Set(detail.members.map((member) => member.email.toLowerCase()));
    const people = [
      ...detail.members.map((member) => ({ ...member, record_type: "member" })),
      ...detail.invites
        .filter((invite) => !invite.accepted_at && !invite.revoked_at
          && !memberEmails.has(invite.email.toLowerCase()))
        .map((invite) => ({ ...invite, record_type: "invite" })),
    ];
    const columns = [
      {
        title: t("admin.common.name"),
        render: (person) => this.personName(person) || t("admin.common.notAvailable"),
      },
      { title: t("admin.common.email"), dataIndex: "email" },
      {
        title: t("admin.common.role"), width: 130,
        render: (person) => {
          const isOrgAdmin = person.record_type === "member"
            ? person.roles.includes("org_admin")
            : person.role === "org_admin";
          return <Tag>{t(isOrgAdmin ? "admin.common.orgAdmin" : "admin.common.member")}</Tag>;
        },
      },
      {
        title: t("admin.common.status"), width: 120,
        render: (person) => {
          if (person.record_type === "member") {
            return person.disabled
              ? <Tag color="red">{t("admin.common.statuses.suspended")}</Tag>
              : <Tag color="green">{t("admin.common.statuses.registered")}</Tag>;
          }
          const status = invitationStatus(person);
          return <Tag color={status === "pending" ? "orange" : "default"}>{t(`admin.common.statuses.${status}`)}</Tag>;
        },
      },
      {
        title: t("admin.organizationDetails.createdInvited"), dataIndex: "created_at", width: 175,
        render: (value) => this.renderTimestamp(value),
      },
      {
        title: t("admin.organizationDetails.lastEmail"), width: 175,
        render: (person) => person.record_type === "invite"
          ? this.renderTimestamp(person.last_sent_at)
          : t("admin.common.notAvailable"),
      },
      {
        title: t("admin.common.action"), width: 160,
        render: (person) => {
          if (person.record_type === "invite") {
            return (
              <Button size="small" onClick={() => this.resendInvite(person)}>
                {t("admin.common.resend")}
              </Button>
            );
          }

          // The API refuses this anyway; hiding it keeps an operator from
          // aiming the irreversible action at their own account.
          if (operator && operator.email === person.email) return null;

          return (
            <Popconfirm
              title={t("admin.organizationDetails.deleteMemberTitle")}
              description={t("admin.organizationDetails.deleteMemberWarning", {
                name: this.personName(person),
              })}
              okText={t("admin.organizationDetails.deleteMemberConfirm")}
              cancelText={t("admin.common.cancel")}
              okButtonProps={{ danger: true, loading: mutationLoading }}
              onConfirm={() => this.deleteMember(person)}
            >
              <Button danger icon={<DeleteOutlined />} size="small">
                {t("admin.organizationDetails.deleteMember")}
              </Button>
            </Popconfirm>
          );
        },
      },
    ];

    return (
      <Wrapper>
        <Space className="toolbar">
          <Button icon={<ArrowLeftOutlined />} onClick={navigateBack}>{t("admin.organizationDetails.back")}</Button>
        </Space>
        <div className="heading-row">
          <Typography.Title level={2} className="page-title">{organization.name}</Typography.Title>
          <Button type="primary" icon={<UserAddOutlined />} onClick={() => this.setState({ inviteOpen: true })}>
            {t("admin.organizationDetails.inviteMember")}
          </Button>
        </div>

        <Descriptions column={{ xs: 1, md: 2 }} bordered>
          <Descriptions.Item label={t("admin.organizations.legalName")}>{organization.legal_name || t("admin.common.notAvailable")}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.taxId")}>{organization.tax_id || t("admin.common.notAvailable")}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.billing")}>
            <Tag color={STATUS_COLORS[organization.billing_status]}>{t(`admin.common.statuses.${organization.billing_status}`)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.communicationLanguage")}>{t(`admin.common.locales.${organization.locale}`)}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.address")}>{organization.address || t("admin.common.notAvailable")}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.city")}>{organization.city || t("admin.common.notAvailable")}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.country")}>
            {new Intl.DisplayNames([i18n.language], { type: "region" }).of(organization.country_code)}
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.timezone")}>{organization.timezone}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.createdAt")}>
            {formatAdminDateTime(organization.created_at, organization.timezone, i18n.language)}
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.trialEnds")}>{formatAdminDate(organization.trial_ends_on, i18n.language)}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizationDetails.invoiceDue")}>{formatAdminDate(organization.invoice_due_on, i18n.language)}</Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.seats")}>
            {t("admin.organizationDetails.seatSummary", {
              registered: organization.seats_taken, reserved: organization.seats_reserved, total: organization.seat_limit,
            })}
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.organizations.notes")} span={2}>{organization.notes || t("admin.common.notAvailable")}</Descriptions.Item>
        </Descriptions>

        <Typography.Title level={4} className="people-title">
          {t("admin.organizationDetails.usersAndInvitations", {
            registered: detail.members.length, total: organization.seat_limit,
          })}
        </Typography.Title>
        <Table
          rowKey={(person) => `${person.record_type}-${person.id}`}
          columns={columns}
          dataSource={people}
          pagination={false}
          scroll={{ x: 1050 }}
        />

        <Modal
          open={inviteOpen}
          title={t("admin.organizations.inviteTitle", { name: organization.name })}
          onOk={this.sendInvite}
          confirmLoading={mutationLoading}
          onCancel={() => this.setState({ inviteOpen: false })}
          okText={t("admin.organizations.sendInvite")}
          cancelText={t("admin.common.cancel")}
        >
          <Form ref={this.inviteFormRef} layout="vertical" initialValues={{ role: "member" }}>
            <Space size="middle" className="form-row">
              <Form.Item label={t("admin.organizations.firstName")} name="first_name" rules={[{ required: true, message: t("admin.organizations.required") }]} className="flex-field">
                <Input autoComplete="given-name" />
              </Form.Item>
              <Form.Item label={t("admin.organizations.lastName")} name="last_name" rules={[{ required: true, message: t("admin.organizations.required") }]} className="flex-field">
                <Input autoComplete="family-name" />
              </Form.Item>
            </Space>
            <Form.Item label={t("admin.common.email")} name="email" rules={[{ required: true, type: "email", message: t("admin.organizations.invalidEmail") }]}>
              <Input autoComplete="email" />
            </Form.Item>
            <Form.Item label={t("admin.common.role")} name="role">
              <Select options={[
                { value: "org_admin", label: t("admin.organizations.masterAccount") },
                { value: "member", label: t("admin.common.member") },
              ]} />
            </Form.Item>
          </Form>
        </Modal>
      </Wrapper>
    );
  }
}

OrganizationDetailsPanel.propTypes = {
  detail: PropTypes.shape({
    organization: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      legal_name: PropTypes.string,
      tax_id: PropTypes.string,
      locale: PropTypes.string.isRequired,
      billing_status: PropTypes.string.isRequired,
      address: PropTypes.string,
      city: PropTypes.string,
      country_code: PropTypes.string.isRequired,
      timezone: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      trial_ends_on: PropTypes.string,
      invoice_due_on: PropTypes.string,
      seats_taken: PropTypes.number.isRequired,
      seats_reserved: PropTypes.number.isRequired,
      seat_limit: PropTypes.number.isRequired,
      notes: PropTypes.string,
    }).isRequired,
    members: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      email: PropTypes.string.isRequired,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string).isRequired,
      disabled: PropTypes.bool.isRequired,
      created_at: PropTypes.string.isRequired,
    })).isRequired,
    invites: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      email: PropTypes.string.isRequired,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      role: PropTypes.string.isRequired,
      pending: PropTypes.bool.isRequired,
      accepted_at: PropTypes.string,
      revoked_at: PropTypes.string,
      created_at: PropTypes.string.isRequired,
      last_sent_at: PropTypes.string,
    })).isRequired,
  }),
  loading: PropTypes.bool.isRequired,
  requestError: PropTypes.string,
  mutationLoading: PropTypes.bool.isRequired,
  feedback: PropTypes.shape({
    type: PropTypes.string.isRequired,
    operation: PropTypes.string,
    message: PropTypes.string,
    messageKey: PropTypes.string,
    values: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    translatedValues: PropTypes.objectOf(PropTypes.string),
  }),
  operator: PropTypes.shape({ email: PropTypes.string.isRequired }),
  fetchOrganization: PropTypes.func.isRequired,
  inviteOrganization: PropTypes.func.isRequired,
  resendInvitation: PropTypes.func.isRequired,
  deleteMember: PropTypes.func.isRequired,
  clearFeedback: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ language: PropTypes.string.isRequired }).isRequired,
  router: PropTypes.shape({
    navigate: PropTypes.func.isRequired,
    params: PropTypes.shape({ organizationSlug: PropTypes.string }).isRequired,
  }).isRequired,
};

OrganizationDetailsPanel.defaultProps = {
  detail: null,
  requestError: null,
  feedback: null,
  operator: null,
};

const mapStateToProps = ({ Admin }) => ({
  detail: Admin.organizationDetail,
  loading: Admin.organizationDetailLoading,
  requestError: Admin.organizationDetailError,
  mutationLoading: Admin.mutationLoading,
  feedback: Admin.feedback,
  operator: Admin.operator,
});

const mapDispatchToProps = (dispatch) => ({
  fetchOrganization: (id) => dispatch(fetchOrganization(id)),
  inviteOrganization: (id, invite, source) => dispatch(inviteOrganization(id, invite, source)),
  resendInvitation: (organizationId, inviteId, email, source) => dispatch(
    resendInvitation(organizationId, inviteId, email, source),
  ),
  deleteMember: (organizationId, userId, displayName) => dispatch(
    deleteMember(organizationId, userId, displayName),
  ),
  clearFeedback: () => dispatch(clearFeedback()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation("common")(withRouter(OrganizationDetailsPanel)));
